/**
 * WS-14 — console GLOBAL SEARCH engine (the pure, testable core).
 *
 * This is a CLIENT-SIDE finder over data the console already holds (the clients/services/staff read
 * hooks), NOT an analytics product and NOT a new API: there is no /v1/search endpoint. The palette
 * (CommandPalette.tsx) feeds this function the already-loaded entities + the debounced query + the
 * acting principal, and renders the bounded list it returns. Keeping the matching here — separate from
 * the dialog/keyboard wiring — is what lets the unit tests assert the filter + RBAC exclusion without a
 * DOM.
 *
 * RBAC (defence in depth): the server's RLS already hides another renter's sealed-book clients from
 * this session (ADR-0005), so the client list the hook returns is pre-filtered. We re-apply the same
 * rule HERE so an entity the role must not see can never surface even if a stale cache, an offline
 * replica, or a future hook change leaked one: a sealed client (`renterId != null`) is included ONLY
 * when it is the acting principal's OWN book (`renterId === user.renterId`). Shared-book clients
 * (`renterId == null`) are visible to everyone in the salon.
 */
import type { Client, Service, Staff } from '@salonomy/types';

/** A flattened, navigable search hit. `to` is a router path; `search` is optional router search. */
export interface SearchResult {
  /** Stable key for React lists + arrow-key focus tracking. */
  key: string;
  kind: 'client' | 'service' | 'staff';
  /** Group heading the row renders under. */
  group: string;
  /** Primary line (e.g. the client/service/staff name). */
  title: string;
  /** Secondary line (e.g. masked phone / category / role) — may be empty. */
  subtitle: string;
  /** Where Enter / click navigates. */
  to: string;
  /** Optional router search params (e.g. deep-linking the clients list to a name). */
  search?: Record<string, string>;
}

/** The acting principal, narrowed to the fields the RBAC rule needs. */
export interface SearchPrincipal {
  renterId: string | null;
}

export interface SearchInput {
  clients: Client[] | undefined;
  services: Service[] | undefined;
  staff: Staff[] | undefined;
  user: SearchPrincipal | null;
}

/** Hard cap so the dialog stays a finder, never an unbounded scroll. */
export const MAX_RESULTS = 8;
/** Per-group cap so one busy entity can't crowd the others out. */
const PER_GROUP = 5;
/** Don't search until the term is meaningful — a single letter matches everything. */
export const MIN_QUERY = 2;

function norm(s: string | null | undefined): string {
  return (s ?? '').toLowerCase().trim();
}

/** Digits only — so "07700 900 123", "+447700900123" and "7700900123" all match the same phone. */
function digits(s: string | null | undefined): string {
  return (s ?? '').replace(/\D+/g, '');
}

/**
 * Is this sealed-book client visible to the acting principal? Shared clients (renterId null) are
 * salon-wide; a sealed client is the acting renter's own book only. This is the RBAC gate the brief
 * calls out — it excludes another renter's sealed-book clients from the results.
 */
export function clientVisibleTo(client: Client, user: SearchPrincipal | null): boolean {
  if (client.renterId == null) return true;
  return user != null && client.renterId === user.renterId;
}

/**
 * Run the finder. Pure: same inputs → same bounded, ordered results. Empty/short query → []. The
 * caller is responsible for debouncing the query feeding this.
 */
export function runSearch(query: string, input: SearchInput): SearchResult[] {
  const q = norm(query);
  if (q.length < MIN_QUERY) return [];
  const qDigits = digits(query);

  const results: SearchResult[] = [];

  // ── Clients — name / phone / email, RBAC-gated to the visible book ──
  const clientHits: SearchResult[] = [];
  for (const c of input.clients ?? []) {
    if (!clientVisibleTo(c, input.user)) continue;
    const name = norm(`${c.firstName} ${c.lastName ?? ''}`);
    const byName = name.includes(q);
    // A phone match needs ≥3 typed digits, else every record with a phone matches a 1–2 digit term.
    const byPhone = qDigits.length >= 3 && digits(c.phone).includes(qDigits);
    const byEmail = norm(c.email).includes(q);
    if (!byName && !byPhone && !byEmail) continue;
    const fullName = [c.firstName, c.lastName].filter(Boolean).join(' ');
    clientHits.push({
      key: `client:${c.id}`,
      kind: 'client',
      group: 'Clients',
      title: fullName,
      subtitle: c.phone ?? c.email ?? '',
      to: '/clients',
      // Deep-link the clients list to this client by name (the ClientsPage reads ?q=).
      search: { q: fullName },
    });
    if (clientHits.length >= PER_GROUP) break;
  }

  // ── Services — name / category ──
  const serviceHits: SearchResult[] = [];
  for (const s of input.services ?? []) {
    if (!s.active) continue;
    if (!norm(s.name).includes(q) && !norm(s.category).includes(q)) continue;
    serviceHits.push({
      key: `service:${s.id}`,
      kind: 'service',
      group: 'Services',
      title: s.name,
      subtitle: s.category ?? '',
      to: '/settings',
    });
    if (serviceHits.length >= PER_GROUP) break;
  }

  // ── Staff — name / title ──
  const staffHits: SearchResult[] = [];
  for (const m of input.staff ?? []) {
    if (!m.active) continue;
    if (!norm(m.displayName).includes(q) && !norm(m.title).includes(q)) continue;
    staffHits.push({
      key: `staff:${m.id}`,
      kind: 'staff',
      group: 'Team',
      title: m.displayName,
      subtitle: m.title ?? roleLabel(m.role),
      to: '/settings',
    });
    if (staffHits.length >= PER_GROUP) break;
  }

  results.push(...clientHits, ...serviceHits, ...staffHits);
  return results.slice(0, MAX_RESULTS);
}

function roleLabel(role: string): string {
  return (
    {
      owner: 'Owner',
      manager: 'Manager',
      stylist: 'Stylist',
      chair_renter: 'Renter',
      receptionist: 'Reception',
    }[role] ?? role
  );
}
